import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Clock, CheckCircle2, BookOpen, ExternalLink } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TemporaryPreviewPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | expired | error
  const [resourceLink, setResourceLink] = useState(null);
  const [content, setContent] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No preview token provided.');
      return;
    }

    const verifyAndFetch = async () => {
      try {
        // Step 1: Verify the temporary access token
        const verifyResponse = await fetch(`${BACKEND_URL}/api/public/temporary-access/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!verifyResponse.ok) {
          const errorData = await verifyResponse.json().catch(() => ({}));
          if (verifyResponse.status === 410 || errorData.detail?.toLowerCase().includes('expired')) {
            setStatus('expired');
            return;
          }
          setStatus('error');
          setErrorMessage(errorData.detail || 'Invalid preview token.');
          return;
        }

        const verifyData = await verifyResponse.json();
        setResourceLink(verifyData.resource_link);
        const tempJwt = verifyData.temporary_jwt;

        // Step 2: Fetch the actual resource content using the temp JWT
        const contentResponse = await fetch(
          `${BACKEND_URL}/api/public/temporary-preview/${verifyData.resource_link.short_code}`,
          {
            headers: { 'Authorization': `Bearer ${tempJwt}` },
          }
        );

        if (!contentResponse.ok) {
          setStatus('error');
          setErrorMessage('Could not load resource content.');
          return;
        }

        const contentData = await contentResponse.json();
        setContent(contentData);
        setStatus('valid');
      } catch (err) {
        console.error('Preview verification error:', err);
        setStatus('error');
        setErrorMessage('An error occurred while loading the preview.');
      }
    };

    verifyAndFetch();
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying your preview access...</p>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Preview Expired</h2>
            <p className="text-gray-600 mb-6">
              Your temporary preview access has expired. Please contact the studio for assistance.
            </p>
            <Button asChild variant="outline">
              <Link to="/">Return to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Preview Unavailable</h2>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <Button asChild variant="outline">
              <Link to="/">Return to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid state - show the resource preview
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Pending approval banner */}
        <Alert className="mb-6 bg-amber-50 border-amber-300">
          <CheckCircle2 className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Your registration is pending approval. You have temporary access to this resource.
          </AlertDescription>
        </Alert>

        {/* Resource preview card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 text-amber-600 mb-2">
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-wide">Resource Preview</span>
            </div>
            <CardTitle className="text-2xl">
              {content?.title || resourceLink?.title || 'Resource'}
            </CardTitle>
            {(content?.description || resourceLink?.description) && (
              <CardDescription className="text-base">
                {content?.description || resourceLink?.description}
              </CardDescription>
            )}
          </CardHeader>

          <CardContent>
            {/* Resource content */}
            {content?.content ? (
              <div className="prose prose-gray max-w-none">
                {typeof content.content === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: content.content }} />
                ) : (
                  <div className="space-y-4">
                    {content.content.title && (
                      <h3 className="text-lg font-semibold">{content.content.title}</h3>
                    )}
                    {content.content.description && (
                      <p className="text-gray-600">{content.content.description}</p>
                    )}
                    {content.content.url && (
                      <a
                        href={content.content.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 underline"
                      >
                        Open Resource <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">No content available for preview.</p>
            )}
          </CardContent>
        </Card>

        {/* Footer note */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Once your registration is approved, you will have full access through the{' '}
          <a
            href="https://members.owazamartialarts.com"
            className="text-amber-700 hover:text-amber-800 underline"
          >
            Member Portal
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default TemporaryPreviewPage;
